import {
  getReadonlyListItemCurrency,
  getReadonlyListItemId,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import type {
  IxcBillingV1ChargeData,
  IxcBillingV1ChargeSpec,
  IxcBillingV1ChargeState,
  IxcBillingV1ChargeTransaction,
  IxcBillingV1CustomerChargeTransaction,
} from '@interxfi/types';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getPortfolioHoldDetailPath } from '../../portfolio/hold/portfolio-hold-routing';
import { getPortfolioTransactionDetailPath } from '../../portfolio/transaction/portfolio-transaction-routing';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getProgramDetailPath } from '../../userauth/program/program-routing';
import { getBillingCorrespondentAccountListItems } from '../billing-shared-list-items';
import { getBillingChargeDetailPath } from './billing-charge-routing';
import { BillingChargeStatusPill } from './billing-charge-status-pill';

export function getBillingChargeSpecListItems(data: IxcBillingV1ChargeSpec | undefined, prefix = '') {
  return {
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    category: getReadonlyListItemString(`${prefix}category`, 'Category', data?.category),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBillingChargeDataListItems(data: IxcBillingV1ChargeData | undefined, accountId: string | undefined, prefix = '') {
  return {
    spec: getBillingChargeSpecListItems(data?.spec, `${prefix}spec`),
    contra: getBillingCorrespondentAccountListItems(data?.contra, `${prefix}contra`),
    customerTransactionId: getReadonlyListItemId(
      `${prefix}customerTransactionId`,
      'Customer Transaction ID',
      data?.customerTransactionId,
      getPortfolioTransactionDetailPath(accountId, data?.customerTransactionId),
    ),
    correspondentTransactionId: getReadonlyListItemId(
      `${prefix}correspondentTransactionId`,
      'Correspondent Transaction ID',
      data?.correspondentTransactionId,
      getPortfolioTransactionDetailPath(data?.contra?.accountId, data?.correspondentTransactionId),
    ),
    holdId: getReadonlyListItemId(`${prefix}holdId`, 'Hold ID', data?.holdId, getPortfolioHoldDetailPath(accountId, data?.holdId)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBillingChargeStateListItems(data: IxcBillingV1ChargeState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    chargeId: getReadonlyListItemId('chargeId', 'Charge ID', data?.chargeId),
    accountId: getReadonlyListItemId('accountId', 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    programId: getReadonlyListItemId('programId', 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcBillingV1',
      enumName: 'IxcBillingV1ChargeStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <BillingChargeStatusPill status={data.status} /> : undefined),
    data: getBillingChargeDataListItems(data?.data, data?.accountId, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBillingChargeTransactionListItems(data: IxcBillingV1ChargeTransaction | undefined, prefix = '') {
  return {
    chargeId: getReadonlyListItemId(`${prefix}chargeId`, 'Charge ID', data?.chargeId, getBillingChargeDetailPath(data?.chargeId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    programId: getReadonlyListItemId(`${prefix}programId`, 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    category: getReadonlyListItemString(`${prefix}category`, 'Category', data?.category),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getBillingCustomerChargeTransactionListItems(data: IxcBillingV1CustomerChargeTransaction | undefined, prefix = '') {
  return {
    chargeId: getReadonlyListItemId(`${prefix}chargeId`, 'Charge ID', data?.chargeId, getBillingChargeDetailPath(data?.chargeId)),
    customerAccountId: getReadonlyListItemId(
      `${prefix}customerAccountId`,
      'Customer Account ID',
      data?.customerAccountId,
      getRegistrationAccountDetailPath(data?.customerAccountId),
    ),
    billingAccountId: getReadonlyListItemId(
      `${prefix}billingAccountId`,
      'Billing Account ID',
      data?.billingAccountId,
      getRegistrationAccountDetailPath(data?.billingAccountId),
    ),
    programId: getReadonlyListItemId(`${prefix}programId`, 'Program ID', data?.programId, getProgramDetailPath(data?.programId)),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    category: getReadonlyListItemString(`${prefix}category`, 'Category', data?.category),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
  } as const satisfies ReadonlyListItemRecord;
}
